import { useEffect, useState } from "react";
import { Line, Bar } from "react-chartjs-2";
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement,
  ArcElement,
  Title, 
  Tooltip, 
  Legend,
  Filler
} from 'chart.js';
import "./Dashboard.css";

// Register chart.js components
ChartJS.register(
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement,
  ArcElement,
  Title, 
  Tooltip, 
  Legend,
  Filler
);

const AdminDashboard = () => {
  // State for statistics
  const [stats, setStats] = useState({
    websiteVisitors: 0,
    todaysUsers: 0,
    revenue: 0,
    conversionRate: 0
  });

  // State for charts data
  const [lineChartData, setLineChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Website Views",
        data: [],
        fill: true,
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        borderColor: "rgba(75, 192, 192, 1)",
        tension: 0.4,
        pointBackgroundColor: "rgba(75, 192, 192, 1)",
        pointBorderColor: "#fff",
        pointHoverRadius: 6,
        pointHoverBorderWidth: 2
      }
    ]
  });

  const [barChartData, setBarChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "User Activity",
        data: [],
        backgroundColor: [
          'rgba(255, 99, 132, 0.7)',
          'rgba(54, 162, 235, 0.7)',
          'rgba(255, 206, 86, 0.7)',
          'rgba(75, 192, 192, 0.7)',
          'rgba(153, 102, 255, 0.7)',
          'rgba(255, 159, 64, 0.7)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)'
        ],
        borderWidth: 1
      }
    ]
  });

  // Generate mock data (replace with your API calls)
  const generateMockData = () => {
    // Stats data
    const newStats = {
      websiteVisitors: Math.floor(Math.random() * 100000) + 50000,
      todaysUsers: Math.floor(Math.random() * 5000) + 1000,
      revenue: Math.floor(Math.random() * 10000) + 5000,
      conversionRate: (Math.random() * 5 + 1).toFixed(2)
    };
    
    // Line chart data
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const lineData = months.map(() => Math.floor(Math.random() * 10000) + 5000);
    
    // Bar chart data
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const barData = days.map(() => Math.floor(Math.random() * 5000) + 1000);
    
    // Pie chart data
    const pieData = [
      Math.floor(Math.random() * 100) + 30,
      Math.floor(Math.random() * 100) + 30,
      Math.floor(Math.random() * 100) + 10
    ];
    
    // Update all states
    setStats(newStats);
    setLineChartData({
      ...lineChartData,
      labels: months,
      datasets: [{
        ...lineChartData.datasets[0],
        data: lineData
      }]
    });
    setBarChartData({
      ...barChartData,
      labels: days,
      datasets: [{
        ...barChartData.datasets[0],
        data: barData
      }]
    });
  };

  // Fetch data on component mount and at regular intervals
  useEffect(() => {
    generateMockData(); // Initial data load

    const interval = setInterval(() => {
      generateMockData(); // Update data every 10 seconds
    }, 10000);

    return () => clearInterval(interval); // Cleanup interval on unmount
  }, []);

  return (
    <div className="dashboard">
      {/* <h1 className="dashboard-title">Admin Dashboard</h1> */}
      
      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card visitors">
          <div className="stat-icon">
            <i className="fas fa-users"></i>
          </div>
          <div className="stat-content">
            <h3>Website Visitors</h3>
            <p className="stat-value">{stats.websiteVisitors.toLocaleString()}</p>
            <p className="stat-change positive">↑ 12.5% from last month</p>
          </div>
        </div>
        
        <div className="stat-card users">
          <div className="stat-icon">
            <i className="fas fa-user-check"></i>
          </div>
          <div className="stat-content">
            <h3>Today's Users</h3>
            <p className="stat-value">{stats.todaysUsers.toLocaleString()}</p>
            <p className="stat-change positive">↑ 3.2% from yesterday</p>
          </div>
        </div>
      </div>
      
      {/* Charts Section */}
      <div className="charts-grid">
        <div className="chart-card line-chart">
          <div className="chart-header">
            <h3>Monthly Traffic Overview</h3>
            <div className="chart-actions">
              <button className="active">12 Months</button>
              <button>6 Months</button>
              <button>30 Days</button>
            </div>
          </div>
          <div className="chart-container">
            <Line 
              data={lineChartData} 
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: {
                      font: {
                        size: 12
                      },
                      boxWidth: 12
                    }
                  },
                  tooltip: {
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    titleFont: {
                      size: 14
                    },
                    bodyFont: {
                      size: 12
                    },
                    callbacks: {
                      label: (tooltipItem) => `${tooltipItem.raw.toLocaleString()} views`
                    }
                  }
                },
                scales: {
                  x: {
                    grid: {
                      display: false
                    }
                  },
                  y: {
                    grid: {
                      color: 'rgba(0,0,0,0.05)'
                    },
                    ticks: {
                      callback: (value) => value.toLocaleString()
                    }
                  }
                },
                elements: {
                  point: {
                    radius: 4,
                    hoverRadius: 6
                  }
                }
              }} 
            />
          </div>
        </div>
        
        <div className="chart-card bar-chart">
          <div className="chart-header">
            <h3>Weekly User Activity</h3>
            <div className="chart-actions">
              <button className="active">This Week</button>
              <button>Last Week</button>
            </div>
          </div>
          <div className="chart-container">
            <Bar 
              data={barChartData} 
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false
                  },
                  tooltip: {
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    callbacks: {
                      label: (tooltipItem) => `${tooltipItem.raw.toLocaleString()} users`
                    }
                  }
                },
                scales: {
                  x: {
                    grid: {
                      display: false
                    }
                  },     
                  y: {
                    grid: {
                      color: 'rgba(0,0,0,0.05)'
                    },
                    ticks: {
                      callback: (value) => value.toLocaleString()
                    }
                  }
                }
              }} 
            />
          </div>
        </div>
        
      </div>
    </div>
  );
};

export default AdminDashboard;